import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { UserContext } from 'Context/user';

import { postMarkAsCollected } from 'Services/privateApis';

import { authorizationHeader } from '../services/apiClient';
import { PostSaleStatusResponse } from '../types/apis.types';

export type UseMarkAsCollected = {
  data?: PostSaleStatusResponse | null;
  error: Error | null;
  loading: boolean;
  markAsCollected: (saleId: string, collected?: boolean) => Promise<void>;
  refetch: () => void;
  statusCode: { current: number };
}

export const useMarkAsCollected = (): UseMarkAsCollected => {
  const { userState: user } = useContext(UserContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<PostSaleStatusResponse | null>(null);
  const [refetch, setRefetch] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const authToken = user?.auth_token;
  const abortController = useMemo(() => new AbortController(), []);
  const cancelled = useRef<boolean>(false);
  const statusCode = useRef<number>(-1);

  useEffect(() => () => {
    cancelled.current = true;
    abortController.abort();
  }, [abortController]);

  const setStatusCode = (status?: number) => {
    if (status) {
      statusCode.current = status;
    }
  };

  const markAsCollected = useCallback(async (saleId: string, collected: boolean = true) => {
    setLoading(true);

    try {
      if (!saleId) {
        throw new Error('Missing sale Id for mark as collected request');
      }

      if (!authToken) {
        throw new Error('Missing auth token for mock mark as collected request');
      }

      const postBody = {
        hasBeenCollected: collected,
      };

      const response = await postMarkAsCollected(
        saleId,
        postBody,
        authorizationHeader(authToken),
        setStatusCode,
        abortController,
      );

      const { signal } = abortController;
      if (signal.aborted) {
        throw new Error('mark as collected request aborted');
      }

      if (!cancelled?.current) {
        setData(response);
      }
    } catch (err) {
      if (!cancelled?.current) {
        setError(err as Error);
        window?.Sentry?.captureException?.(err, {
          extra: { saleId },
        });
      }
    } finally {
      if (!cancelled?.current) {
        setLoading(false);
      }
    }
  }, [authToken, abortController]);

  return {
    data,
    error,
    loading,
    markAsCollected,
    refetch: () => setRefetch(refetch + 1),
    statusCode,
  };
};
